import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Button, Dialog, DialogContent, Grid, IconButton, LinearProgress, linearProgressClasses, Stack, styled, TextField, Typography } from "@mui/material";
import { Add, Check, Close, FiberManualRecord, WatchLater } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import Tutor from "./Tutor";
import Events from "./Events";
import SessionHistory from "./SessionHistory";
import RecentActivites from "./RecentActivities";
import CommonCard from "../../common/components/CommonCard";
import Tasks from "./Tasks";
import IntakeProfile from "./IntakeProfile";
import ActionPlan from "./ActionPlan";
import CurrentPlan from "../../common/components/CurrentPlan";
import { ExamTypes, ProgressStatusObj, ServiceOffer, UserTypeId } from "../../common/const";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { checkBookingSession, confirmBooking, getEventByInvite } from "../../api/sessionApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonBookSession from "../../common/components/CommonBookSession";
import moment from "moment";
import { getCounsellorDashboard } from "../../api/mentorApi";
import theme from "../../theme";

const BorderLinearProgress = styled(LinearProgress)(({ barcolor, theme }) => ({
    height: 6,
    borderRadius: 5,
    marginTop: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: barcolor,
    },
}));

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const DcpCounsellor = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [assigned, setAssigned] = useState(null);
    const [mentorId, setMentorId] = useState(null);
    const [meetingLink, setMeetingLink] = useState(null);
    const [open, setOpen] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');
    const [stat, setStat] = useState(null);
    const [coachData, setCoachData] = useState(null);

    const checkSessionBook = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await checkBookingSession(ServiceOffer.counsellor, user?.student_id);
            setMentorId(response?.data?.mentor_profile_id);
            setMeetingLink(response?.data?.calendly_link);
            setAssigned(response?.data.type);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const getCounsellorData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getCounsellorDashboard(user?.student_id);
            setStat(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            checkSessionBook();
            getCounsellorData();
        }
    }, [user]);

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);

    const handleCloseAll = () => {
        setBookSessionDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "mentor_profile_id": mentorId,
                "student_id": user?.student_id,
                "service_offers": [ServiceOffer.counsellor],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                getCounsellorData();
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const mentorData = coachData?.mentor?.studentAssignedMentor.find(
        (a) => a.counsellor_suggestion !== null
    );

    const progressColor =
        mentorData?.counsellor_provided_status === ProgressStatusObj.OnTrack
            ? "#10AE2D"
            : mentorData?.counsellor_provided_status === ProgressStatusObj.Needs
                ? theme.palette.primary.main
                : "red";

    const backgroundColor =
        mentorData?.counsellor_provided_status === ProgressStatusObj.OnTrack
            ? "#DCFCE7"
            : mentorData?.counsellor_provided_status === ProgressStatusObj.Needs
                ? '#FEF9C2'
                : "#FFE2E2";

    const progressValue =
        mentorData?.counsellor_provided_status === ProgressStatusObj.OnTrack
            ? 100
            : mentorData?.counsellor_provided_status === ProgressStatusObj.Needs
                ? 50
                : 25;

    return (
        <Fragment>
            <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#6B7280',
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <img
                                    src='/Images/graycalendar.png'
                                    style={{
                                        width: 20,
                                        position: 'relative'
                                    }}
                                    alt=''
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Date
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Time
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            Any specific question for this session
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Write here"
                            value={sessionNote}
                            onChange={(e) => setSessionNote(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        fullWidth
                        onClick={handleConfirmBooking}
                        sx={{
                            backgroundColor: '#1E3A8A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1E40AF',
                            },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>
            <CommonBookSession
                bookingUrl={meetingLink}
                open={open}
                setOpen={setOpen}
                studentName={`${user?.first_name} ${user?.last_name}`}
                email={user?.email}
                handleClose={(data) => {
                    setOpen(false);
                    setInitialData(data);
                }}
            />
            <PageHeader
                title="Counseling Hub"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            My DCP Services
                        </Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            Counseling Hub
                        </Typography>
                    </Stack>
                }
                buttonContent={
                    (assigned !== 'DISCOVERY' && user?.user_type_id === UserTypeId.Student) && (
                        <CommonButton
                            width="165px"
                            label="Book New Session"
                            fontWeight={600}
                            startIcon={<Add />}
                            marginBottom="0px"
                            handleClick={() => {
                                setOpen(true);
                            }}
                        />
                    )}
            />
            <Grid container spacing={2} mt={2} mb={2}>
                <Grid item md={8}>
                    {coachData?.mentor?.studentAssignedMentor?.length > 0 && mentorData?.counsellor_suggestion && (
                        <CommonCard
                            paddingBottom="0px"
                            borderRadius={2}
                            marginTop="0px"
                            background={backgroundColor}
                            content={
                                <Box display="flex" gap={1} alignItems="center">
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        background: progressColor,
                                        width: 70,
                                        height: 70,
                                        position: 'relative'
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            border: '2px solid white',
                                            width: 30,
                                            height: 30
                                        }}>
                                            <Check sx={{ color: 'white', fontSize: 14 }} />
                                        </Box>
                                    </Box>
                                    <Box width="90%">
                                        <Typography color={progressColor} fontSize={16} fontWeight={600}>
                                            {mentorData?.counsellor_suggestion}
                                        </Typography>

                                        <Typography color="background.dark" fontSize={13}>
                                            All required Document submitted
                                        </Typography>

                                        <BorderLinearProgress
                                            variant="determinate"
                                            value={progressValue}
                                            barcolor={progressColor}
                                        />
                                    </Box>
                                </Box>
                            }
                        />
                    )}
                    <Tasks stat={stat} />
                    <Tutor
                        assigned={assigned}
                        mentorId={mentorId}
                        getCounsellorData={getCounsellorData}
                        coachData={coachData}
                        setCoachData={setCoachData}
                        setInitialData={setInitialData}
                    />
                    <IntakeProfile />
                    <ActionPlan getCounsellorData={getCounsellorData} />
                </Grid>
                <Grid item md={4}>
                    <CurrentPlan
                        examType={ExamTypes.COUNSELLING}
                    />
                    <Events 
                      setInitialData={setInitialData}
                    />
                    <SessionHistory />
                    <RecentActivites />
                </Grid>
            </Grid>
        </Fragment>
    )
}

export default DcpCounsellor;